import {
    Alert,
    DialogTitle,
    Typography,
    DialogContent,
    TextField,
    Button,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Stack,
    useTheme,
} from "@mui/material";
import { useState } from "react";
import { IncidentErrors } from "../apiClients/IncidentClient";
import { useErrorDisplay } from "../components/ErrorDisplay"
import { DefaultDateTimeOffset, FormatDate, GetUpsertIcon, IsEmpty, IsNotEmpty, IsNotNullOrUndefined, IsNullOrUndefined } from "../Helpers";
import BusySpinner from "../components/BusySpinner";
import IconButtonEx, { IconButtonExProps } from "../components/IconButtonEx";
import { UpsertAction } from "../Enums";
import dayjs from "dayjs";
import { ActiveState, Incident, PerformanceItem, SeverityLevel, Talent, IncidentReview } from "../models/dtos";
import TalentClient, { TalentErrors } from "../apiClients/TalentClient";
import ConditionalDisplay from "../components/ConditionalDisplay";
import TalentAutocompleteControl from "../components/TalentAutocompleteControl";
import TableHeaderCell from "../components/TableHeaderCell";
import useViewportMode from "../hooks/useViewportMode";
import { MobileEntityCard, MobileFormStack, ResponsiveDialog, ResponsiveDialogHeader, StickyDialogActions } from "../components/responsive";

export interface PerformanceItemUpsertProps extends Omit<IconButtonExProps, 'onClick'> {
    mode: UpsertAction;
    inTalentId?: string;
    inTalent?: Talent;
    performanceItemId?: string;
    inPerformanceItem?: PerformanceItem;
    onChange?: () => Promise<void>;
    onAdd?: (id: string) => Promise<void>;
    onResolve?: (id: string) => Promise<void>;
    onClose?: () => void;
    icon?: JSX.Element;
}

const PerformanceItemUpsertPopup = ({ mode, inTalentId, inTalent, performanceItemId, inPerformanceItem, onResolve = async () => { }, onChange = async () => { }, onAdd = async () => { }, onClose = () => { }, icon = GetUpsertIcon(mode), ...btnProps }: PerformanceItemUpsertProps) => {

    const [ErrorDisplay, setError] = useErrorDisplay();
    const [busy, setBusy] = useState<boolean>(false);
    const [open, setOpen] = useState<boolean>(false);
    const theme = useTheme();
    const viewport = useViewportMode();
    const useIncidentCards = viewport.isPhone || (viewport.isTablet && viewport.isPortrait);

    const [performanceItem, setPerformanceItem] = useState<PerformanceItem>(null);
    const [incidents, setIncidents] = useState<IncidentReview[]>([]);
    const [doResolve, setDoResolve] = useState<boolean>(false);
    const lockTalentSelection = IsNotEmpty(inTalentId) || IsNotNullOrUndefined(inTalent?.Id);

    const title = () => {
        if (mode === UpsertAction.View) {
            return 'Review Performance Challenge';
        }
        else {
            return (mode === UpsertAction.Create) ? 'Create Performance Improvement Challenge' : `Mark Challenge Accomplished`;
        }
    }

    const isNegativeIncident = (type?: string | null) =>
        (type ?? '').toLowerCase().includes('negative');

    const handleSubmit = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                await createPerformanceItem();
            }
            else if (mode === UpsertAction.Update) {
                await new TalentClient().ResolvePerformanceItem(performanceItem.Id);
                await onResolve(performanceItem.Id);
            }
            setOpen(false);
            onClose();
        } catch (error) {
            setError(error, mode === UpsertAction.Create ? TalentErrors.CreatePII : IncidentErrors.Update);
        }
        finally {
            setBusy(false);
        }
    };

    const createPerformanceItem = async () => {
        let assignIncidentIds = incidents
            .filter(x => x.apply)
            .map(m => m.Id);

        let pi = {
            Title: performanceItem.Title,
            Description: performanceItem.Description,
            Severity: performanceItem.Severity,
            IncidentIds: assignIncidentIds
        }

        const talentId = inTalentId ?? inTalent?.Id ?? performanceItem?.TalentId;
        if (IsEmpty(talentId))
            throw new Error('A teammate is required to create a performance item.');
        if (assignIncidentIds.length === 0)
            throw new Error('Select at least one negative event to create a performance item.');

        let id = await new TalentClient().CreatePerformanceItem(talentId, pi);
        await onAdd(id);
    }

    const disableSave = mode === UpsertAction.Create
        ? IsEmpty(inTalentId ?? inTalent?.Id ?? performanceItem?.TalentId)
            || IsEmpty(performanceItem?.Title)
            || IsEmpty(performanceItem?.Description)
            || !incidents.some(incident => incident.apply)
        : mode === UpsertAction.Update ? !doResolve : true;

    const handleInputChange = e => {
        const { name, value } = e.target
        setPerformanceItem(current => ({ ...current, [name]: value }))
    };

    const handleOpen = async () => {
        setOpen(true);
        await loadData();
    };

    const handleClose = () => {
        setOpen(false);
        onClose();
    };

    const loadData = async () => {
        try {
            setBusy(true);

            if (mode === UpsertAction.Create) {
                setPerformanceItem({
                    Id: '',
                    TalentId: inTalentId ?? inTalent?.Id ?? '',
                    SystemUserId: '',
                    Title: '',
                    Description: '',
                    Severity: SeverityLevel.Low,
                    State: ActiveState.Active,
                    ActiveDate: dayjs().format(),
                    ResolvedDate: '',
                    IsDeleted: false,
                    LastUpdateDate: DefaultDateTimeOffset,
                    Incidents: []
                });

                const tid = inTalentId ?? inTalent?.Id;
                let incidentDisplayList: IncidentReview[] = [];

                if (IsNotEmpty(tid)) {
                    let i = await new TalentClient().GetAllIncidents(tid);
                    i.filter(f => IsEmpty(f.PerformanceItemId) && isNegativeIncident(f.Type))
                        .forEach(x => {
                            let incidentReview = {
                                Id: x.Id,
                                IsDeleted: x.IsDeleted,
                                LastUpdateDate: x.LastUpdateDate,
                                TalentId: x.TalentId,
                                Type: x.Type,
                                Title: x.Title,
                                Description: x.Description,
                                OccurrenceDate: x.OccurrenceDate,
                                apply: false,
                                isReviewed: false
                            }
                            incidentDisplayList.push(incidentReview);
                        });
                }

                setIncidents(incidentDisplayList);
            }
            // else if (mode === UpsertAction.Update) {
            //     if (IsNotNullOrUndefined(inPerformanceItem)) {
            //         setPerformanceItem(inPerformanceItem);
            //     }
            // }
             else {
                setPerformanceItem(inPerformanceItem);
                setIncidents(transformIncidents(inPerformanceItem.Incidents));
            }

        } catch (error) {
            setError(error, IncidentErrors.GetSingle);
        }
        finally {
            setBusy(false);
        }
    };

    const handleTalentSelect = (id: string) => {
        setPerformanceItem(current => ({ ...current, TalentId: id }));
    }

    const handleIncidentSelected = (incident: IncidentReview, e) => {
        updateIncidentInState(incident, 'apply', e.target.checked);
    }

    const handleResolveCheckChange = (checked: boolean) => {
        setDoResolve(checked);
    }

    const updateIncidentInState = (incident: IncidentReview, propName: string, propValue) => {
        setIncidents(current => current.map(item => item.Id === incident.Id
            ? { ...item, [propName]: propValue }
            : item));
    }

    const transformIncidents = (incidents: Incident[]) => {

        let incidentDisplayList: IncidentReview[] = [];

        incidents.forEach(x => {
            let incidentReview = {
                Id: x.Id,
                IsDeleted: x.IsDeleted,
                LastUpdateDate: x.LastUpdateDate,
                TalentId: x.TalentId,
                Type: x.Type,
                Title: x.Title,
                Description: x.Description,
                OccurrenceDate: x.OccurrenceDate,
                apply: false,
                isReviewed: false
            };
            incidentDisplayList.push(incidentReview);
        });

        return incidentDisplayList;
    }

    return <>
        <ErrorDisplay />
        <IconButtonEx disabled={busy} hoverContent={title()} onClick={handleOpen} {...btnProps}>{icon}</IconButtonEx>

        <ResponsiveDialog aria-labelledby="performance-item-upsert-title" open={open} fullWidth onClose={handleClose} tabletFullScreen="portrait" PaperProps={{ sx: { overflow: 'hidden', '& > div': { flex: '1 1 auto', minHeight: 0, height: 'auto !important', display: 'flex', flexDirection: 'column' } } }}>
            {viewport.isDesktop ? (
                <DialogTitle id="performance-item-upsert-title" sx={{ margin: 0, padding: theme.spacing(2) }}>
                    <Typography sx={{ fontSize: 'Large' }}>{title()}</Typography>
                </DialogTitle>
            ) : (
                <ResponsiveDialogHeader id="performance-item-upsert-title" title={title()} onClose={handleClose} sx={{ margin: 0, padding: theme.spacing(2) }} />
            )}
            <BusySpinner busy={busy}>
                <DialogContent dividers sx={{ padding: theme.spacing(2), overflowY: 'auto' }}>
                    <MobileFormStack tabletColumns={2} desktopColumns={1} sx={{ gap: viewport.isDesktop ? 0 : 2 }}>
                            <Box sx={{ marginBottom: theme.spacing(2) }}>
                                <ConditionalDisplay ifTrue={mode === UpsertAction.View || IsNotNullOrUndefined(inTalent?.LastName)}>
                                    <Typography>{`${inTalent?.FirstName} ${inTalent?.LastName}`}</Typography>
                                </ConditionalDisplay>
                            <ConditionalDisplay ifTrue={mode === UpsertAction.Create && IsNullOrUndefined(inTalent?.LastName)}>
                                    <TalentAutocompleteControl
                                        talentId={performanceItem?.TalentId}
                                        placeholderText="Who needs improvement?"
                                        onUserSelect={handleTalentSelect}
                                        readOnly={lockTalentSelection}
                                    />
                            </ConditionalDisplay>
                            </Box>

                            <TextField label="Title" fullWidth
                                name="Title"
                                disabled={mode === UpsertAction.View}
                                error={IsEmpty(performanceItem?.Title)}
                                margin="normal"
                                variant="outlined"
                                value={performanceItem?.Title}
                                onChange={handleInputChange} />
                            <TextField label="Improvement Plan" fullWidth
                                name="Description"
                                disabled={mode === UpsertAction.View}
                                error={IsEmpty(performanceItem?.Description)}
                                margin="normal"
                                variant="outlined"
                                multiline={true}
                                placeholder="enter the plan here"
                                value={performanceItem?.Description}
                                onChange={handleInputChange}
                                sx={{ gridColumn: { sm: '1 / -1', lg: 'auto' } }} />
                            <ConditionalDisplay ifTrue={mode === UpsertAction.Create }>
                                <Box sx={{ marginBottom: theme.spacing(2) }}>
                                    <InputLabel id="severity-select-label">Severity</InputLabel>
                                    <Select fullWidth
                                        labelId="severity-select-label"
                                        id="severity-select"
                                        name="Severity"
                                        value={performanceItem?.Severity ?? SeverityLevel.Low}
                                        onChange={handleInputChange}>
                                        <MenuItem value={SeverityLevel.Low}>Low</MenuItem>
                                        <MenuItem value={SeverityLevel.Medium}>Medium</MenuItem>
                                        <MenuItem value={SeverityLevel.High}>High</MenuItem>
                                    </Select>
                                </Box>
                            </ConditionalDisplay>
                            <ConditionalDisplay ifTrue={mode === UpsertAction.View || mode === UpsertAction.Update}>
                                <TextField label="Severity"
                                    disabled={true}
                                    margin="normal"
                                    variant="outlined"
                                    value={performanceItem?.Severity?.toString()} />
                                    </ConditionalDisplay>
                            <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                                <Box sx={{ marginBottom: theme.spacing(2), gridColumn: { sm: '1 / -1', lg: 'auto' } }}>
                                    <Typography>Is Active<Checkbox disabled={true} name="activeState" checked={true} inputProps={{ 'aria-label': 'primary checkbox' }} /></Typography>
                                    <ConditionalDisplay ifTrue={mode === UpsertAction.Update}>
                                        <Typography>Has the performance been sufficiently improved?<Checkbox name="doResolve" checked={doResolve} onChange={(e) => handleResolveCheckChange(e.target.checked)} inputProps={{ 'aria-label': 'primary checkbox' }} /></Typography>
                                    </ConditionalDisplay>
                                </Box>
                            </ConditionalDisplay>
                            <ConditionalDisplay ifTrue={mode === UpsertAction.View}>
                                <Box sx={{ marginBottom: theme.spacing(2) }}>
                                    <Typography>Is Resolved<Checkbox disabled={true} name="activeState" checked={true} inputProps={{ 'aria-label': 'primary checkbox' }} /></Typography>
                                </Box>
                            </ConditionalDisplay>
                    </MobileFormStack>
                    <Box sx={{ mt: 2 }}>
                            <ConditionalDisplay ifTrue={mode === UpsertAction.Create}>
                                <Box>
                                    <Typography>Select Incident(s) to create this Performance Improvement Item from: </Typography>
                                </Box>
                            </ConditionalDisplay>
                            {mode === UpsertAction.Create && incidents.length === 0 && <Alert severity="info" sx={{ mt: 1 }}>
                                No available negative events. Add or link a negative event before creating a performance item.
                            </Alert>}
                            {useIncidentCards ? (
                                <Stack spacing={1.5} component="section" aria-label="Related incidents" sx={{ mt: 1 }}>
                                    {incidents.map((incident) => (
                                        <MobileEntityCard
                                            key={incident.Id}
                                            title={incident.Title}
                                            subtitle={FormatDate(incident.OccurrenceDate)}
                                            status={incident.Type}
                                            actions={mode === UpsertAction.Create ? (
                                                <FormControlLabel
                                                    control={<Checkbox name="apply" checked={incident.apply} onChange={(event) => handleIncidentSelected(incident, event)} inputProps={{ 'aria-label': `Select negative event ${incident.Title}` }} />}
                                                    label="Select"
                                                    sx={{ m: 0 }}
                                                />
                                            ) : undefined}
                                            ariaLabel={`Incident ${incident.Title}`}
                                        />
                                    ))}
                                </Stack>
                            ) : (
                            <TableContainer style={{ border: '1', borderRadius: '5px' }}>
                                <Table size="small" aria-label="a dense table">
                                    <TableHead>
                                        <TableRow>
                                            <ConditionalDisplay ifTrue={mode === UpsertAction.Create}>
                                                <TableHeaderCell style={{ width: 20 }}>
                                                    <Typography>
                                                        Select
                                                    </Typography>
                                                </TableHeaderCell>
                                            </ConditionalDisplay>
                                            <TableHeaderCell style={{ width: 20 }}>Type</TableHeaderCell>
                                            <TableHeaderCell style={{ width: 20 }}>Title</TableHeaderCell>
                                            <TableHeaderCell style={{ width: 20 }}>Date</TableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {incidents.map((i) => (
                                            <TableRow key={i.Id}>
                                                <ConditionalDisplay ifTrue={mode === UpsertAction.Create}>
                                                    <TableCell>
                                                        <Checkbox name="apply" checked={i.apply} onChange={(e) => handleIncidentSelected(i, e)} inputProps={{ 'aria-label': `Select negative event ${i.Title}` }} />
                                                    </TableCell>
                                                </ConditionalDisplay>
                                                <TableCell>{i.Type}</TableCell>
                                                <TableCell>{i.Title}</TableCell>
                                                <TableCell>{FormatDate(i.OccurrenceDate)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            )}
                    </Box>
                </DialogContent>
                <StickyDialogActions phoneLayout="stacked" sx={{ margin: 0, padding: theme.spacing(1) }}>
                    <Button onClick={handleClose} variant='text' color='inherit'>Cancel</Button>
                    <ConditionalDisplay ifTrue={mode === UpsertAction.Create || mode === UpsertAction.Update}>
                        <Button disabled={disableSave} onClick={handleSubmit} variant='outlined' color='primary'>{(mode === UpsertAction.Create) ? 'Submit' : 'Resolve'}</Button>
                    </ConditionalDisplay>
                </StickyDialogActions>
            </BusySpinner>
        </ResponsiveDialog>
    </>;
}

export default PerformanceItemUpsertPopup;
